import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { ivrTypeLabels } from './IvrNode';
import { IvrType, ttsModelLabels } from '../../types/ivr';
import { Button } from '../../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, ConsoleTextArea } from '../../styles/common/input.styled';
import { statusColors } from '../../styles/consoleTheme';
import { LoadingIcon } from '../common/Icons';
import { IvrSettingsFields, IvrSettingsPanel } from './IvrSettingsPanel';

const Choices = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
`;
const TtsBillingNotice = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: -12px 0 -8px;
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => statusColors[theme.name].warning};
  span { flex: 0 0 auto; font-size: 14px; line-height: 18px; }
`;
const TtsKeyNotice = styled.button`
  display: flex; align-items: flex-start; gap: 4px; width: 100%; margin: -12px 0 -8px; padding: 0;
  border: 0; background: transparent; color: ${({ theme }) => statusColors[theme.name].warning};
  font: inherit; font-size: 12px; line-height: 18px; text-align: left; cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
  span { flex: 0 0 auto; font-size: 14px; line-height: 18px; }
`;
const LocateLink = styled.button`
  margin-top: 8px; padding: 0; border: 0; background: none;
  color: ${({ theme }) => theme.color.point_y_100}; cursor: pointer;
  font: inherit; font-size: 12px; text-decoration: underline; text-underline-offset: 3px;
  &:hover { color: ${({ theme }) => theme.color.point_y_200}; text-decoration-thickness: 2px; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 3px; }
`;

interface Props {
  nodeId: string;
  nodeData: Record<string, any>;
  audios: any[];
  ringingGroups: any[];
  endpoints: any[];
  onUpdate: (nodeId: string, updates: Record<string, any>) => void;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
  onCancel: () => void;
  previewTts: (config: any) => Promise<any>;
  downloadAudio: (config: any) => Promise<any>;
  geminiTtsKeyConfigured?: boolean;
  onOpenTtsApiKey?: () => void;
  onLocateReturnTarget?: (nodeId: string) => void;
}

const IvrNodeSettings = ({ nodeId, nodeData, audios, ringingGroups, endpoints, onUpdate, onDirtyChange, onClose, onCancel, previewTts, downloadAudio, geminiTtsKeyConfigured, onOpenTtsApiKey, onLocateReturnTarget }: Props) => {
  const [initial] = useState(() => ({
    custom_title: nodeData.custom_title || '',
    audio_type: nodeData.audio_type || (nodeData.audio_file_id ? 'AUDIO' : nodeData.tts_text ? 'TTS' : 'AUDIO'),
    audio_file_id: nodeData.audio_file_id || '',
    audio_filename: nodeData.audio_filename || '',
    tts_text: nodeData.tts_text || '',
    tts_model: nodeData.tts_model ?? 'LOCAL',
    ringing_group_id: nodeData.ringing_group_id || '',
    ringing_group_display_name: nodeData.ringing_group_display_name || '',
    direct_number: nodeData.direct_number || '', direct_name: nodeData.direct_name || '',
    condition_days: nodeData.condition_days || [],
    condition_start_time: nodeData.condition_start_time || '09:00',
    condition_end_time: nodeData.condition_end_time || '18:00',
    condition_dates: (nodeData.condition_dates || '').split(',').map((date: string) => date.trim()).filter(Boolean),
    condition_holiday: nodeData.condition_holiday === 'Y', repeat_count: nodeData.repeat_count || 1,
    return_target: nodeData.return_target || 'PREVIOUS',
  }));
  const [draft, setDraft] = useState(initial);
  const [dateInput, setDateInput] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewVersion = useRef(0);
  const previewUrl = useRef<string | null>(null);
  const type = nodeData.ivr_type;
  const isAudio = type === 'ARS' || type === 'PLAY';
  const isGeminiTts = draft.audio_type === 'TTS' && String(draft.tts_model || '').startsWith('GEMINI');
  const isGeminiKeyMissing = isGeminiTts && geminiTtsKeyConfigured === false;

  useEffect(() => { onDirtyChange(JSON.stringify(initial) !== JSON.stringify(draft) || Boolean(dateInput)); }, [initial, draft, dateInput, onDirtyChange]);
  const stopAudio = () => {
    previewVersion.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
  };
  useEffect(() => () => stopAudio(), []);
  const change = (key: keyof typeof initial, value: any) => {
    stopAudio(); setIsPlaying(false); setIsPreviewLoading(false);
    setDraft(previous => ({ ...previous, [key]: value }));
  };
  const play = async () => {
    if (isPlaying) { stopAudio(); setIsPlaying(false); setIsPreviewLoading(false); return; }
    const version = ++previewVersion.current;
    setIsPlaying(true);
    setIsPreviewLoading(true);
    try {
      const blob = draft.audio_type === 'AUDIO'
        ? await downloadAudio({ url: `/audio-source/file/${draft.audio_file_id}`, method: 'GET', responseType: 'blob' })
        : await previewTts({ url: '/audio-source/tts', method: 'POST', data: { tts_text: draft.tts_text, tts_model: draft.tts_model || 'LOCAL', lang: 'ko' }, responseType: 'blob' });
      if (version !== previewVersion.current) return;
      setIsPreviewLoading(false);
      previewUrl.current = URL.createObjectURL(new Blob([blob]));
      const audio = new Audio(previewUrl.current);
      audioRef.current = audio;
      audio.onended = () => { stopAudio(); setIsPlaying(false); setIsPreviewLoading(false); };
      audio.onerror = () => { stopAudio(); setIsPlaying(false); setIsPreviewLoading(false); };
      await audio.play();
    } catch {
      if (version !== previewVersion.current) return;
      stopAudio(); setIsPlaying(false); setIsPreviewLoading(false); toast.error('음원 재생에 실패했습니다.');
    }
  };
  const apply = () => {
    const updates: Record<string, any> = { custom_title: draft.custom_title.trim() || undefined };
    if (isAudio) {
      if (draft.audio_type === 'TTS' && (draft.tts_text.length < 5 || draft.tts_text.length > 300)) {
        toast.error('TTS 텍스트는 5~300자로 입력하세요.'); return;
      }
      Object.assign(updates, draft.audio_type === 'AUDIO' ? {
        audio_type: 'AUDIO', audio_file_id: draft.audio_file_id, audio_filename: draft.audio_filename,
        tts_text: undefined, tts_model: undefined, voice: undefined, speed: undefined,
      } : {
        audio_type: 'TTS', tts_text: draft.tts_text, tts_model: draft.tts_model || 'LOCAL',
        audio_file_id: undefined, audio_filename: undefined, voice: undefined, speed: undefined,
      });
    }
    if (type === 'RINGING_GROUP') Object.assign(updates, { ringing_group_id: draft.ringing_group_id ? Number(draft.ringing_group_id) : undefined, ringing_group_display_name: draft.ringing_group_display_name });
    if (type === 'DIRECT') Object.assign(updates, { direct_number: draft.direct_number, direct_name: draft.direct_name });
    if (type.startsWith('CONDITION')) Object.assign(updates, {
      condition_days: draft.condition_days, condition_start_time: draft.condition_start_time,
      condition_end_time: draft.condition_end_time, condition_dates: draft.condition_dates.join(', '),
      condition_holiday: draft.condition_holiday ? 'Y' : 'N',
    });
    if (type === 'REPEAT') updates.repeat_count = draft.repeat_count;
    if (type === 'MENU_RETURN') updates.return_target = draft.return_target;
    stopAudio(); setIsPlaying(false); setIsPreviewLoading(false); onUpdate(nodeId, updates);
  };
  const addDate = () => {
    if (!dateInput) return;
    if (draft.condition_dates.includes(dateInput)) { toast.error('이미 추가된 날짜입니다.'); return; }
    if (draft.condition_dates.length >= 20) { toast.error('날짜는 최대 20개까지만 설정할 수 있습니다.'); return; }
    change('condition_dates', [...draft.condition_dates, dateInput].sort()); setDateInput('');
  };

  return <IvrSettingsPanel aria-label="선택 노드 설정">
    <header><h2>{nodeData.custom_title?.trim() || nodeData.name} 설정</h2><Button $variant="ghost" aria-label="노드 설정 닫기" onClick={onClose}>×</Button></header>
    <IvrSettingsFields>
      <small>{ivrTypeLabels[type as IvrType] || nodeData.name}</small>
      <ConsoleLabel>사용자 지정 제목<ConsoleInput autoFocus value={draft.custom_title} onChange={event => change('custom_title', event.target.value)} placeholder="비워두면 기본 제목을 사용합니다" /></ConsoleLabel>
      {isAudio && <>
        <Choices>{['AUDIO', 'TTS'].map(mode => <Button key={mode} $variant={draft.audio_type === mode ? 'primary' : 'secondary'} aria-pressed={draft.audio_type === mode} onClick={() => change('audio_type', mode)}>{mode === 'AUDIO' ? '음원 파일' : 'TTS (음성 합성)'}</Button>)}</Choices>
        {draft.audio_type === 'AUDIO' ? <ConsoleLabel>음원 선택<ConsoleSelect value={draft.audio_file_id} onChange={event => {
          const audio = audios.find(item => item.id === Number(event.target.value));
          change('audio_file_id', audio?.id || ''); change('audio_filename', audio?.name || audio?.originalFilename || audio?.original_filename || '');
        }}><option value="">음원 선택</option>{audios.map(item => <option key={item.id} value={item.id}>{item.name || item.originalFilename || item.original_filename}</option>)}</ConsoleSelect></ConsoleLabel> : <>
          <ConsoleLabel>TTS 텍스트 (5~300자)<ConsoleTextArea rows={6} value={draft.tts_text} maxLength={300} onChange={event => change('tts_text', event.target.value)} /></ConsoleLabel>
          <small>{draft.tts_text.length} / 300</small>
          <ConsoleLabel>TTS 모델<ConsoleSelect value={draft.tts_model} onChange={event => change('tts_model', event.target.value)}>{Object.entries(ttsModelLabels).map(([model, label]) => <option key={model} value={model}>{label}</option>)}</ConsoleSelect></ConsoleLabel>
          {isGeminiTts && (isGeminiKeyMissing ? <TtsKeyNotice type="button" onClick={onOpenTtsApiKey} disabled={!onOpenTtsApiKey}><span aria-hidden="true">⚠</span><span>GEMINI API KEY를 먼저 추가해주세요.</span></TtsKeyNotice> : <TtsBillingNotice role="note"><span aria-hidden="true">⚠</span><span>해당 모델은 미리듣기 시, 비용이 과금됩니다.</span></TtsBillingNotice>)}
        </>}
        <Button $variant="secondary" aria-busy={isPreviewLoading} disabled={draft.audio_type === 'AUDIO' ? !draft.audio_file_id : draft.tts_text.length < 5 || isGeminiKeyMissing} onClick={play}>
          {isPreviewLoading && <LoadingIcon size={14} />}
          {isPlaying ? '미리듣기 중지' : '미리듣기'}
        </Button>
      </>}
      {type === 'RINGING_GROUP' && <ConsoleLabel>수신그룹 선택<ConsoleSelect value={draft.ringing_group_id} onChange={event => {
        const group = ringingGroups.find(item => item.id === Number(event.target.value));
        change('ringing_group_id', group?.id || ''); change('ringing_group_display_name', group?.displayName || '');
      }}><option value="">수신그룹 선택</option>{ringingGroups.map(item => <option key={item.id} value={item.id}>{item.displayName}</option>)}</ConsoleSelect></ConsoleLabel>}
      {type === 'DIRECT' && <ConsoleLabel>연결 대상 선택<ConsoleSelect value={draft.direct_number} onChange={event => {
        change('direct_number', event.target.value); change('direct_name', endpoints.find(item => item.phone === event.target.value)?.displayName || '');
      }}><option value="">직접연결 선택</option>{endpoints.map(item => <option key={item.phone} value={item.phone}>{item.displayName}</option>)}</ConsoleSelect></ConsoleLabel>}
      {type === 'CONDITION_DAY' && <div><ConsoleLabel>요일 선택</ConsoleLabel><Choices>{Object.entries({ MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일' }).map(([day, label]) => <Button key={day} $size="sm" aria-pressed={draft.condition_days.includes(day)} $variant={draft.condition_days.includes(day) ? 'primary' : 'secondary'} onClick={() => change('condition_days', draft.condition_days.includes(day) ? draft.condition_days.filter((value: string) => value !== day) : [...draft.condition_days, day])}>{label}</Button>)}</Choices></div>}
      {type === 'CONDITION_TIME' && <><ConsoleLabel>시작 시간<ConsoleInput type="time" value={draft.condition_start_time} onChange={event => change('condition_start_time', event.target.value)} /></ConsoleLabel><ConsoleLabel>종료 시간<ConsoleInput type="time" value={draft.condition_end_time} onChange={event => change('condition_end_time', event.target.value)} /></ConsoleLabel></>}
      {type === 'CONDITION_DATE' && <>
        <ConsoleLabel>특정 날짜 설정 (최대 20개)<ConsoleInput type="date" value={dateInput} onChange={event => setDateInput(event.target.value)} /></ConsoleLabel>
        <Button $variant="secondary" onClick={addDate} disabled={!dateInput}>날짜 추가</Button>
        <Choices>{draft.condition_dates.map((date: string) => <Button $variant="secondary" $size="sm" key={date} aria-label={`${date} 삭제`} onClick={() => change('condition_dates', draft.condition_dates.filter((value: string) => value !== date))}>{date} ×</Button>)}</Choices>
      </>}
      {type === 'CONDITION_HOLIDAY' && <ConsoleLabel style={{ flexDirection: 'row' }}><input style={{ width: 'auto' }} type="checkbox" checked={draft.condition_holiday} onChange={event => change('condition_holiday', event.target.checked)} />공휴일 체크 활성화</ConsoleLabel>}
      {type === 'REPEAT' && <div><ConsoleLabel>반복 횟수 (1~3회)</ConsoleLabel><Choices>{[1, 2, 3].map(count => <Button key={count} aria-pressed={draft.repeat_count === count} $variant={draft.repeat_count === count ? 'primary' : 'secondary'} onClick={() => change('repeat_count', count)}>{count}회</Button>)}</Choices></div>}
      {type === 'MENU_RETURN' && <div><ConsoleLabel>이동 대상<ConsoleSelect value={draft.return_target} onChange={event => change('return_target', event.target.value)}><option value="PREVIOUS">이전 ARS 메뉴</option><option value="START">처음부터 다시 시작</option></ConsoleSelect></ConsoleLabel>{draft.return_target === 'PREVIOUS' && <LocateLink type="button" onClick={() => onLocateReturnTarget?.(nodeId)}>어디로 이동하나요?</LocateLink>}</div>}
    </IvrSettingsFields>
    <footer><Button $variant="secondary" onClick={onCancel}>취소</Button><Button $variant="primary" onClick={apply} disabled={Boolean(dateInput)} title={dateInput ? '선택한 날짜를 추가하거나 지우세요' : undefined}>노드 적용</Button></footer>
  </IvrSettingsPanel>;
};
export default IvrNodeSettings;
